import { Editor } from "ketcher-react";
import "ketcher-react/dist/index.css";
import { StandaloneStructServiceProvider } from "ketcher-standalone";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useKetcher } from "../../hooks/useKetcher";
import useTableStore from "../../store/useTabStore";
import ErrorModal from "../ui/errorModal";
import SearchConfigurationPanel from "./structureSearchOptions";
import TemplatePanel from "./templatePanels";

const structService = new StandaloneStructServiceProvider();

const StructureSearch: React.FC = () => {
  const currentKetcherSmilesRef = useRef<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentKetcherSmiles, setCurrentKetcherSmiles] = useState<
    string | null
  >(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const [searchOptions, setSearchOptions] = useState("substructure");
  const [similarity, setSimilarity] = useState("0.05");
  const [templateInsertMode, setTemplateInsertMode] = useState<
    "append" | "replace"
  >("append");
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const { setStructureSearchParams } = useTableStore();
  const {
    wrapperRef: ketcherWrapperRef,
    isKetcherInitialized,
    initKetcherHandler,
    setMoleculeOnKetcher,
    getSmilesFromKetcher,
    addFragmentToKetcher,
  } = useKetcher({
    onStructureDidChange: (smiles: string) => {
      if (smiles !== currentKetcherSmilesRef.current) {
        setCurrentKetcherSmiles(smiles);
      }
    },
  });

  const closeOtherDropdowns = useCallback((currentId: string | null) => {
    setOpenDropdownId(currentId);
  }, []);

  const handleResetSearchAndEditor = useCallback(async () => {
    if (isKetcherInitialized) {
      try {
        await setMoleculeOnKetcher("");
      } catch (error) {
        console.error("Error clearing Ketcher editor:", error);
        setErrorModalMessage("An error occurred while resetting the editor.");
        setIsErrorModalOpen(true);
      }
    }
    setCurrentKetcherSmiles(null);
    setSearchOptions("substructure");
    setSimilarity("0.05");

    const newParams = new URLSearchParams(searchParams.toString());
    newParams.delete("type");
    newParams.delete("smiles");
    newParams.delete("threshold");
    setSearchParams(newParams, { replace: true });

    closeOtherDropdowns(null);
  }, [
    isKetcherInitialized,
    setMoleculeOnKetcher,
    searchParams,
    setSearchParams,
    closeOtherDropdowns,
  ]);

  const handleTemplateSelect = useCallback(
    async (smiles: string) => {
      if (!isKetcherInitialized) {
        setErrorModalMessage(
          "The chemical editor is not yet initialized. Please wait a moment."
        );
        setIsErrorModalOpen(true);
        return;
      }
      try {
        if (templateInsertMode === "replace") {
          await setMoleculeOnKetcher(smiles);
          setCurrentKetcherSmiles(smiles);
        } else {
          await addFragmentToKetcher(smiles);
          const updatedSmiles = await getSmilesFromKetcher();
          setCurrentKetcherSmiles(updatedSmiles);
        }
      } catch (error) {
        console.error("Error loading template to Ketcher:", error);
        setErrorModalMessage("Failed to load the template into the editor.");
        setIsErrorModalOpen(true);
      }
    },
    [
      isKetcherInitialized,
      templateInsertMode,
      setMoleculeOnKetcher,
      addFragmentToKetcher,
      getSmilesFromKetcher,
    ]
  );

  const handlePerformSearch = useCallback(async () => {
    if (!isKetcherInitialized) {
      setErrorModalMessage(
        "The chemical editor is not ready. Please try again shortly."
      );
      setIsErrorModalOpen(true);
      return;
    }

    let smilesToSearch = "";
    try {
      smilesToSearch = await getSmilesFromKetcher();
      if (smilesToSearch !== currentKetcherSmilesRef.current) {
        setCurrentKetcherSmiles(smilesToSearch);
      }
    } catch (error) {
      console.error("Error getting SMILES for search:", error);
      setErrorModalMessage(
        "An error occurred while retrieving the structure from the editor."
      );
      setIsErrorModalOpen(true);
      return;
    }

    if (!smilesToSearch) {
      setErrorModalMessage(
        "Please draw a structure or select a template to perform a search."
      );
      setIsErrorModalOpen(true);
      return;
    }
    if (smilesToSearch.includes(".")) {
      setErrorModalMessage(
        "The structure appears to have disconnected parts. Please use a single, connected molecule for this search."
      );
      setIsErrorModalOpen(true);
      return;
    }
    if (!searchOptions) {
      setErrorModalMessage(
        "Please select a search option (e.g., Substructure, Similarity)."
      );
      setIsErrorModalOpen(true);
      return;
    }

    try {
      const structParams = {
        type: searchOptions,
        smiles: encodeURIComponent(smilesToSearch),
        threshold:
          searchOptions === "similarity" ? parseFloat(similarity) : undefined,
      };
      setStructureSearchParams(structParams);

      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set("type", structParams.type);
      newParams.set("smiles", structParams.smiles);
      if (structParams.threshold !== undefined) {
        newParams.set("threshold", structParams.threshold.toString());
      } else {
        newParams.delete("threshold");
      }
      setSearchParams(newParams, { replace: true });
    } catch (error) {
      console.error("Search Error:", error);
      const message = error instanceof Error ? error.message : String(error);
      setErrorModalMessage(`An error occurred during the search: ${message}`);
      setIsErrorModalOpen(true);
    }
  }, [
    isKetcherInitialized,
    getSmilesFromKetcher,
    searchOptions,
    similarity,
    setStructureSearchParams,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    currentKetcherSmilesRef.current = currentKetcherSmiles;
  }, [currentKetcherSmiles]);

  useEffect(() => {
    if (isKetcherInitialized) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        (document.activeElement as HTMLElement)?.blur();
      }
    }
  }, [isKetcherInitialized]);

  useEffect(() => {
    const typeParam = searchParams.get("type");
    const urlSmilesParam = searchParams.get("smiles");
    const thresholdParam = searchParams.get("threshold");

    if (typeParam) setSearchOptions(typeParam);
    if (typeParam === "similarity" && thresholdParam) {
      setSimilarity(thresholdParam);
    }

    if (!isKetcherInitialized || !setMoleculeOnKetcher) return;

    const decodedUrlSmiles = urlSmilesParam
      ? decodeURIComponent(urlSmilesParam)
      : null;

    if (decodedUrlSmiles) {
      if (decodedUrlSmiles !== currentKetcherSmilesRef.current) {
        setMoleculeOnKetcher(decodedUrlSmiles)
          .then(() => setCurrentKetcherSmiles(decodedUrlSmiles))
          .catch((e) => console.error("Failed to set molecule from URL:", e));
      }
    } else if (
      urlSmilesParam === "" &&
      currentKetcherSmilesRef.current !== null
    ) {
      setMoleculeOnKetcher("")
        .then(() => setCurrentKetcherSmiles(null))
        .catch((e) =>
          console.error(
            "Error clearing Ketcher from empty smiles URL param:",
            e
          )
        );
    }
  }, [searchParams, isKetcherInitialized, setMoleculeOnKetcher]);

  return (
    <div className="flex rounded-lg bg-gray-50">
      <TemplatePanel
        onTemplateSelect={handleTemplateSelect}
        selectedSmilesInKetcher={currentKetcherSmiles}
        templateInsertMode={templateInsertMode}
        onTemplateInsertModeChange={setTemplateInsertMode}
      />
      <div className="flex flex-col flex-1 h-full p-2">
        <div
          ref={ketcherWrapperRef}
          className="md:h-[600px] w-full mb-4 bg-white rounded-lg shadow-md flex-shrink-0"
        >
          <Editor
            staticResourcesUrl={process.env.PUBLIC_URL || ""}
            errorHandler={(e) => console.error("Ketcher Editor Error:", e)}
            structServiceProvider={structService}
            onInit={initKetcherHandler}
            buttons={useKetcher().ketcherButtons}
          />
        </div>
        <div className="p-4 bg-white rounded-lg shadow-md">
          <SearchConfigurationPanel
            searchOptions={searchOptions}
            onSearchOptionsChange={setSearchOptions}
            similarity={similarity}
            onSimilarityChange={setSimilarity}
            onPerformSearch={handlePerformSearch}
            onResetSearch={handleResetSearchAndEditor}
            openDropdownId={openDropdownId}
            closeOtherDropdowns={closeOtherDropdowns}
          />
          <p className="mt-2 text-xs text-right text-gray-400">
            Scroll down to see selected formulations
          </p>
        </div>
      </div>
      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        message={errorModalMessage}
        title="Search Alert"
      />
    </div>
  );
};

export default StructureSearch;
